import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Chip, Typography, Box, TextField, Tooltip, CircularProgress, Button,
  Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import Home from './Home';
import SocieteDialog from './SocieteDialog';
import {
  Societe, listerSocietes, supprimerSociete, normaliserDomaine, resumerLiens, messageErreur
} from '../services/societesServices';

// Menu « Sociétés » : CRUD de la table societes. Les clients dont l'email est sur le domaine d'une société
// paient toujours par virement (la colonne paiementCB n'est pas gérée).

const SocietesScreen: React.FC = () => {
  const [societes, setSocietes] = useState<Societe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Societe | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Société de la fenêtre de suppression, gardée pendant l'animation de fermeture (texte stable)
  const [aSupprimer, setASupprimer] = useState<Societe | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      setSocietes(await listerSocietes());
    } catch (e) {
      console.error('Erreur chargement sociétés', e);
      setSocietes([]);
      setToast({ msg: messageErreur(e, 'Chargement des sociétés impossible (backend redémarré depuis l\'ajout du menu ?)'), sev: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return societes.filter((s) => s.nom.toLowerCase().includes(q) || s.mail.toLowerCase().includes(q));
  }, [societes, search]);

  const openEdit = (s: Societe | null) => {
    setSelected(s);
    setDialogOpen(true);
  };

  const demanderSuppression = (s: Societe) => {
    setASupprimer(s);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!aSupprimer) return;
    const s = aSupprimer;
    setConfirmOpen(false);
    try {
      await supprimerSociete(s.id);
      setToast({ msg: `Société « ${s.nom} » supprimée`, sev: 'success' });
      fetchAll();
    } catch (e) {
      console.error('Erreur suppression société', e);
      setToast({ msg: messageErreur(e, 'Suppression impossible'), sev: 'error' });
    }
  };

  const liensBloquants = aSupprimer ? resumerLiens(aSupprimer) : '';

  return (
    <>
      <Home />
      <Container maxWidth={false} sx={{ mt: 2, px: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Sociétés</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Sociétés clientes et leur domaine email : les clients inscrits avec une adresse sur ce domaine paient par virement.
          </Typography>
          <TextField label="Rechercher" size="small" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEdit(null)}>
            Nouvelle société
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Société</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Domaine email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Comptes clients</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell sx={{ fontWeight: 500, minWidth: 160 }}>{s.nom}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      {s.mail || (
                        <Chip label="domaine manquant" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {s.mail ? `${s.comptes_domaine} sur @${normaliserDomaine(s.mail)}` : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small" color="primary" onClick={() => openEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => demanderSuppression(s)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {societes.length ? 'Aucune société ne correspond à la recherche' : 'Aucune société : créez la première avec « Nouvelle société »'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <SocieteDialog
        open={dialogOpen}
        societe={selected}
        societes={societes}
        onClose={() => setDialogOpen(false)}
        onSaved={(msg) => {
          setToast({ msg, sev: 'success' });
          fetchAll();
        }}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{liensBloquants ? 'Suppression impossible' : 'Confirmez la suppression'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {liensBloquants
              ? `« ${aSupprimer ? aSupprimer.nom : ''} » est encore liée à ${liensBloquants}. Détachez-les avant de supprimer la société.`
              : `Supprimer définitivement « ${aSupprimer ? aSupprimer.nom : ''} » ? Les clients sur @${aSupprimer ? normaliserDomaine(aSupprimer.mail) : ''} ne paieront plus par virement.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">{liensBloquants ? 'Fermer' : 'Annuler'}</Button>
          {!liensBloquants && <Button onClick={handleDelete} color="error" autoFocus>Supprimer</Button>}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default SocietesScreen;
